import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { publicApi } from '../lib/api';
import { Loader2, Users, Search, ChevronDown, CheckCircle, XCircle, Shield, User, Hash } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import SearchableCombobox from './SearchableCombobox';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';  // same-origin by default

const VIEW_MODES = [
  { key: 'general', label: 'Geral' },
  { key: 'weight', label: 'Categoria de Peso' },
  { key: 'team', label: 'Equipe' },
  { key: 'solo', label: 'Atletas Sozinhos' },
];

// Rótulo amigável de modalidade
const MODALITY_LABELS = { gi: 'KIMONO', nogi: 'NO-GI' };

// Centralized category display: strip sex suffix from age_class, uppercase all, join with ' - '
const _SEX_SUFFIX_RE = /\s+(MASC|FEM|MASCULINO|FEMININO)\s*$/i;
function categoryDisplay(ageClass, belt, weightCategory, sexCode, modality) {
  const cleanAge = (ageClass || '').replace(_SEX_SUFFIX_RE, '').trim();
  const sexLabel = { F: 'FEMININO', M: 'MASCULINO', MF: 'MISTO' }[sexCode] || '';
  const modMap = { gi: 'KIMONO', nogi: 'NO-GI' };
  const modLabel = modMap[(modality || '').toLowerCase()] || '';
  return [cleanAge, (belt || '').toUpperCase(), (weightCategory || '').toUpperCase(),
          sexLabel, modLabel].filter(Boolean).join(' - ');
}


// Ordem canônica oficial das faixas (mesma ordem do seed de belts no backend).
// FASE D — as faixas infantis COM BARRA são faixas definidas (como Azul/Preta):
// Branca → cinzas → amarelas → laranjas → verdes → adultas.
const BELT_ORDER = [
  'BRANCA',
  'CINZA & BRANCA', 'CINZA', 'CINZA & PRETA',
  'AMARELA & BRANCA', 'AMARELA', 'AMARELA & PRETA',
  'LARANJA & BRANCA', 'LARANJA', 'LARANJA & PRETA',
  'VERDE E BRANCA', 'VERDE', 'VERDE E PRETA',
  'AZUL', 'ROXA', 'MARROM', 'PRETA', 'CORAL', 'VERMELHA',
];

// Ordem canônica oficial das categorias de peso
const WEIGHT_CATEGORY_ORDER = ['GALO', 'PLUMA', 'PENA', 'LEVE', 'MÉDIO', 'MEIO PESADO', 'PESADO', 'SUPER-PESADO', 'PESADISSIMO'];

// Ordena valores pela ordem canônica (case-insensitive); valores desconhecidos
// vão para o final em ordem alfabética para nunca sumirem da lista.
const sortByCanonicalOrder = (values, order) => {
  const norm = (v) => (v || '').toUpperCase().trim();
  return [...values].sort((a, b) => {
    const ia = order.indexOf(norm(a));
    const ib = order.indexOf(norm(b));
    if (ia === -1 && ib === -1) return norm(a).localeCompare(norm(b));
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
};

const PaymentBadge = ({ status }) => {
  const ok = status === 'confirmed';
  return (
    <span className={`inline-flex items-center gap-1 text-micro font-medium px-1.5 py-0.5 rounded ${
      ok ? 'bg-green-500/15 text-green-400 border border-green-500/40'
         : 'bg-red-400/15 text-red-300 border border-red-400/40'
    }`}>
      {ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
      {ok ? 'Pagamento Confirmado' : 'Pagamento Não Confirmado'}
    </span>
  );
};

/**
 * Row card with the athlete metadata shared across all three view modes.
 *
 * ETAPA VISUAL 6 — DUAS COLUNAS. Antes cada atleta ocupava 5 linhas rotuladas
 * empilhadas ("Categoria:", "Equipe:", "Professor:", "Inscrição #") — ~134 a
 * 160px por atleta, o que numa lista de centenas vira rolagem longa. Agora o
 * nome e a categoria ficam à esquerda e os metadados viram CHIPS à direita, na
 * mesma faixa horizontal (no celular volta a empilhar).
 *
 * A zebra por índice (idx % 2) foi mantida como estava.
 */
const ChipAtleta = ({ icone: Icone, children, className = '' }) => (
  <span
    className={`inline-flex max-w-[220px] items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1A] px-2 py-1 text-micro text-[#A1A1AA] ${className}`}
  >
    {Icone && <Icone size={12} className="shrink-0 text-[#D4AF37]" aria-hidden="true" />}
    <span className="truncate">{children}</span>
  </span>
);

const AthleteRow = ({ a, idx, showCategory = true, showTeam = true, peers = null, onPeersClick }) => {
  const categoria = categoryDisplay(
    a.athlete_age_class,
    a.athlete_belt,
    a.weight_category,
    a.athlete_sex,
    a.modality,
  );

  return (
    <div
      className={`px-4 py-3 ${idx % 2 === 1 ? 'bg-[#181818]' : 'bg-[#141414]'} hover:bg-[#1F1F1F] transition-colors`}
      data-testid={`public-athlete-row-${a._id}`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        {/* Coluna 1 — quem é */}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">{a.athlete_name}</div>
          {showCategory && (a.weight_category || a.athlete_age_class) && (
            <div className="mt-0.5 truncate text-xs text-[#D4AF37]">{categoria}</div>
          )}
        </div>

        {/* Coluna 2 — os metadados como chips */}
        <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
          {showTeam && a.team_name && <ChipAtleta icone={Shield}>{a.team_name}</ChipAtleta>}
          {a.professor_name && <ChipAtleta icone={User}>{a.professor_name}</ChipAtleta>}
          <ChipAtleta icone={Hash}>{a.registration_number}</ChipAtleta>
          {peers != null && (
            <button
              type="button"
              onClick={() => onPeersClick?.(a)}
              className="inline-flex items-center rounded-lg border border-[#D4AF37]/40 px-2 py-1 text-micro text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
              data-testid={`public-athlete-peers-${a._id}`}
            >
              Atletas {peers}
            </button>
          )}
          <PaymentBadge status={a.payment_status} />
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all border ${
      active
        ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
        : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#D4AF37]/40 hover:text-white'
    }`}
    data-testid={`public-athletes-view-${active ? 'active' : 'inactive'}`}
  >
    {children}
    {count != null && (
      <span className={`ml-2 text-xs ${active ? 'opacity-80' : 'opacity-60'}`}>({count})</span>
    )}
  </button>
);

export function PublicAthleteList({ championshipId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('general');
  const [search, setSearch] = useState('');
  const [filterBelt, setFilterBelt] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSex, setFilterSex] = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [peersDialog, setPeersDialog] = useState(null);

  // Canonical key used to determine "same category": same age_class + belt +
  // weight_category + sex. Athletes from any team that share this key are
  // direct competitors.
  const categoryKeyOf = useCallback((a) => {
    return [a.athlete_age_class || '', a.athlete_belt || '', a.weight_category || '', a.athlete_sex || '', a.modality || ''].join('|');
  }, []);

  // Build a map { categoryKey -> [athletes] } from the FULL list so peer counts
  // are not affected by current filters/search.
  const peersByCategory = useMemo(() => {
    const map = new Map();
    for (const a of list) {
      const k = categoryKeyOf(a);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    }
    return map;
  }, [list, categoryKeyOf]);

  const peerCountOf = useCallback((a) => {
    const arr = peersByCategory.get(categoryKeyOf(a));
    return arr ? arr.length : 0;
  }, [peersByCategory, categoryKeyOf]);

  const openPeers = useCallback((athlete) => {
    const peers = peersByCategory.get(categoryKeyOf(athlete)) || [];
    setPeersDialog({ athlete, peers });
  }, [peersByCategory, categoryKeyOf]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.getRegistrations(championshipId);
      setList(data || []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => { load(); }, [load]);

  // Filter selector options derived from the data, ordered by the official
  // canonical order (belt colors and weight categories), not alphabetically.
  const allBelts = useMemo(
    () => sortByCanonicalOrder(new Set(list.map((a) => a.athlete_belt).filter(Boolean)), BELT_ORDER),
    [list],
  );
  const allCategories = useMemo(
    () => sortByCanonicalOrder(new Set(list.map((a) => a.weight_category).filter(Boolean)), WEIGHT_CATEGORY_ORDER),
    [list],
  );
  const hasModality = useMemo(() => list.some((a) => a.modality), [list]);
  const allModalities = useMemo(
    () => sortByCanonicalOrder(
      new Set(list.map((a) => a.modality).filter(Boolean)),
      ['gi', 'nogi'],
    ),
    [list],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((a) => {
      if (q && !`${a.athlete_name} ${a.team_name || ''}`.toLowerCase().includes(q)) return false;
      if (filterBelt && a.athlete_belt !== filterBelt) return false;
      if (filterCategory && a.weight_category !== filterCategory) return false;
      if (filterSex && a.athlete_sex !== filterSex) return false;
      if (filterModality && a.modality !== filterModality) return false;
      return true;
    });
  }, [list, search, filterBelt, filterCategory, filterSex, filterModality]);

  // GENERAL mode: alphabetical groups by first letter of the name
  const generalGroups = useMemo(() => {
    const map = new Map();
    const sorted = [...filtered].sort((a, b) => (a.athlete_name || '').localeCompare(b.athlete_name || ''));
    for (const a of sorted) {
      const letter = (a.athlete_name || '?').charAt(0).toUpperCase();
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter).push(a);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // WEIGHT mode: groups by category_name+age+belt+sex
  const weightGroups = useMemo(() => {
    const map = new Map();
    for (const a of filtered) {
      const key = categoryDisplay(a.athlete_age_class, a.athlete_belt, a.weight_category, a.athlete_sex, a.modality) || 'Sem categoria';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // TEAM mode: cards by team
  const teamGroups = useMemo(() => {
    const map = new Map();
    for (const a of filtered) {
      const key = a.team_id || 'no_team';
      if (!map.has(key)) {
        map.set(key, {
          team_id: a.team_id || null,
          team_name: a.team_name || 'Sem equipe',
          team_logo: a.team_logo,
          athletes: [],
        });
      }
      map.get(key).athletes.push(a);
    }
    return Array.from(map.values()).sort((x, y) => (x.team_name || '').localeCompare(y.team_name || ''));
  }, [filtered]);

  // SOLO mode: categories with exactly 1 athlete (computed from FULL list, not filtered)
  const soloCategories = useMemo(() => {
    const result = [];
    for (const [key, athletes] of peersByCategory) {
      if (athletes.length === 1) {
        const a = athletes[0];
        const catLabel = categoryDisplay(a.athlete_age_class, a.athlete_belt, a.weight_category, a.athlete_sex, a.modality);
        result.push({ key, category: catLabel, athlete: a });
      }
    }
    result.sort((x, y) => x.category.localeCompare(y.category));
    return result;
  }, [peersByCategory]);

  const generateSoloPDF = useCallback(() => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = 20;
    const left = 20;
    const colN = left, colAthlete = left + 18, colCat = left + 85, colTeam = left + 195;
    const rowH = 7;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('Atletas Sozinhos na Chave', left, y); y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text('Total: ' + soloCategories.length + ' atleta(s) sozinho(s)', left, y); y += 10;

    // Header
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(212, 175, 55);
    doc.rect(left, y - 5, pageW - left * 2, 8, 'F');
    doc.text('Nº', colN + 2, y); doc.text('Atleta', colAthlete + 2, y);
    doc.text('Categoria Completa', colCat + 2, y); doc.text('Equipe', colTeam + 2, y);
    y += 10;
    doc.setTextColor(0, 0, 0);

    soloCategories.forEach((item, idx) => {
      if (y > pageH - 20) {
        doc.addPage(); y = 20;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
        doc.setTextColor(255, 255, 255); doc.setFillColor(212, 175, 55);
        doc.rect(left, y - 5, pageW - left * 2, 8, 'F');
        doc.text('Nº', colN + 2, y); doc.text('Atleta', colAthlete + 2, y);
        doc.text('Categoria Completa', colCat + 2, y); doc.text('Equipe', colTeam + 2, y);
        y += 10; doc.setTextColor(0, 0, 0);
      }
      if (idx % 2 === 0) { doc.setFillColor(245, 245, 245); doc.rect(left, y - 4, pageW - left * 2, rowH, 'F'); }
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(String(idx + 1), colN + 2, y);
      doc.text((item.athlete.athlete_name || '').substring(0, 50), colAthlete + 2, y);
      doc.text((item.category || '').substring(0, 60), colCat + 2, y);
      doc.text((item.athlete.team_name || '').substring(0, 40), colTeam + 2, y);
      y += rowH;
    });

    doc.save('atletas_sozinhos.pdf');
  }, [soloCategories]);

  const generateSoloExcel = useCallback(() => {
    const wsData = [['Nº', 'Nome do Atleta', 'Categoria Completa', 'Equipe']];
    soloCategories.forEach((item, idx) => {
      wsData.push([idx + 1, item.athlete.athlete_name || '', item.category, item.athlete.team_name || '']);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 5 }, { wch: 35 }, { wch: 50 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Atletas Sozinhos');
    XLSX.writeFile(wb, 'atletas_sozinhos.xlsx');
  }, [soloCategories]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
      </div>
    );
  }

  const total = filtered.length;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16" data-testid="public-athletes-section">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-5">
        <Users className="w-6 h-6 text-[#D4AF37]" />
        <h2 className="text-xl sm:text-2xl font-bold text-white font-['Chivo']">Atletas Inscritos</h2>
        <span className="text-[#A1A1AA] text-sm">· {list.length} total</span>
      </div>

      {/* View mode pills */}
      <div className="flex flex-wrap gap-2 mb-5" role="tablist">
        {VIEW_MODES.map((m) => (
          <TabButton key={m.key} active={mode === m.key} onClick={() => setMode(m.key)}>
            {m.label}
          </TabButton>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-5">
        <div className="relative lg:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar atleta ou equipe…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-sm placeholder:text-[#71717A] focus:border-[#D4AF37] focus:outline-none"
            data-testid="public-athletes-search"
          />
        </div>
        <SearchableCombobox
          value={filterBelt}
          onChange={setFilterBelt}
          options={allBelts.map((b) => ({ value: b, label: b }))}
          placeholder="Todas as faixas"
          searchPlaceholder="Buscar faixa…"
          emptyText="Nenhuma faixa encontrada"
          allowClear
          testId="public-filter-belt"
        />
        <SearchableCombobox
          value={filterCategory}
          onChange={setFilterCategory}
          options={allCategories.map((c) => ({ value: c, label: c }))}
          placeholder="Todas categorias"
          searchPlaceholder="Buscar categoria…"
          emptyText="Nenhuma categoria encontrada"
          allowClear
          testId="public-filter-category"
        />
        <select
          value={filterSex}
          onChange={(e) => setFilterSex(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-sm focus:border-[#D4AF37] focus:outline-none"
          data-testid="public-filter-sex"
        >
          <option value="">Todos os gêneros</option>
          <option value="M">Masculino</option>
          <option value="F">Feminino</option>
        </select>
        {hasModality && (
          <select
            value={filterModality}
            onChange={(e) => setFilterModality(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#141414] border border-[#27272A] text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            data-testid="public-filter-modality"
          >
            <option value="">Todas modalidades</option>
            <option value="gi">Kimono (Gi)</option>
            <option value="nogi">No-Gi</option>
          </select>
        )}
      </div>

      {/* Total */}
      <div className="text-xs text-[#71717A] mb-3">
        Total de <span className="text-white font-bold">{total}</span> {total === 1 ? 'atleta' : 'atletas'} na visualização atual
      </div>

      {total === 0 ? (
        <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
          Nenhum atleta encontrado com os filtros atuais.
        </div>
      ) : mode === 'general' ? (
        <div className="space-y-5">
          {generalGroups.map(([letter, athletes]) => (
            <div key={letter} data-testid={`public-letter-group-${letter}`}>
              <div className="flex items-baseline gap-2 mb-2 border-b border-[#27272A] pb-1">
                <span className="text-3xl font-black text-[#D4AF37] font-['Chivo']">{letter}</span>
                <span className="text-xs text-[#71717A]">{athletes.length}</span>
              </div>
              <div className="rounded-xl overflow-hidden border border-[#27272A]">
                {athletes.map((a, idx) => (
                  <AthleteRow key={a._id} a={a} idx={idx} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : mode === 'weight' ? (
        <div className="space-y-5">
          {weightGroups.map(([key, athletes]) => (
            <div key={key} data-testid={`public-weight-group-${key}`}>
              <div className="flex items-baseline gap-2 mb-2 border-b border-[#27272A] pb-1">
                <h3 className="text-lg font-bold text-white font-['Chivo']">{key}</h3>
                <span className="text-xs text-[#71717A]">{athletes.length} atletas</span>
              </div>
              <div className="rounded-xl overflow-hidden border border-[#27272A]">
                {athletes.map((a, idx) => (
                  <AthleteRow key={a._id} a={a} idx={idx} showCategory={false} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {teamGroups.map((g) => {
            const isOpen = expandedTeam === g.team_id;
            return (
              <div key={g.team_id || g.team_name} className="rounded-xl border border-[#27272A] bg-[#141414] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedTeam(isOpen ? null : g.team_id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-[#181818] transition-colors text-left"
                  data-testid={`public-team-card-${g.team_id || 'no'}`}
                >
                  <div className="w-14 h-14 rounded-lg bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center overflow-hidden shrink-0">
                    {g.team_logo ? (
                      <img src={`${API_URL}/api/files/${g.team_logo}`} alt={g.team_name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-[#71717A] font-bold tracking-wider px-1 text-center">
                        {g.team_name.slice(0, 3).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold uppercase tracking-wide truncate font-['Chivo']">{g.team_name}</div>
                    <div className="text-xs text-[#A1A1AA]">{g.athletes.length} {g.athletes.length === 1 ? 'atleta inscrito' : 'atletas inscritos'}</div>
                  </div>
                  <ChevronDown size={20} className={`text-[#71717A] transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-[#27272A]" data-testid={`public-team-expanded-${g.team_id || 'no'}`}>
                    {g.athletes.map((a, idx) => (
                      <AthleteRow
                        key={a._id}
                        a={a}
                        idx={idx}
                        showTeam={false}
                        peers={peerCountOf(a)}
                        onPeersClick={openPeers}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* SOLO tab content */}
      {mode === 'solo' && (
        <div data-testid="public-solo-athletes">
          {soloCategories.length === 0 ? (
            <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              Nenhum atleta encontrado sozinho em categoria.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  type="button"
                  onClick={generateSoloPDF}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#E5C158] transition-colors"
                  data-testid="solo-pdf-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Gerar PDF
                </button>
                <button
                  type="button"
                  onClick={generateSoloExcel}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-colors"
                  data-testid="solo-excel-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
                  Gerar Excel
                </button>
              </div>
              <div className="rounded-xl overflow-hidden border border-[#27272A]">
                {soloCategories.map((item, idx) => (
                  <div
                    key={item.key}
                    className={`px-4 py-3 ${idx % 2 === 1 ? 'bg-[#181818]' : 'bg-[#141414]'} hover:bg-[#1F1F1F] transition-colors`}
                    data-testid={`public-solo-row-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-sm">{item.athlete.athlete_name}</div>
                        <div className="mt-1 flex flex-col gap-y-0.5 text-xs text-[#A1A1AA]">
                          <span>
                            <span className="text-[#71717A]">Categoria: </span>
                            <span className="text-[#D4AF37]">{item.category}</span>
                          </span>
                          {item.athlete.team_name && (
                            <span>
                              <span className="text-[#71717A]">Equipe: </span>{item.athlete.team_name}
                            </span>
                          )}
                          {item.athlete.registration_number && (
                            <span>
                              <span className="text-[#71717A]">Inscrição #</span>{item.athlete.registration_number}
                            </span>
                          )}
                        </div>
                      </div>
                      <PaymentBadge status={item.athlete.payment_status} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Peers Dialog — opened from the "Atletas X" link inside team view */}
      <Dialog open={!!peersDialog} onOpenChange={(o) => !o && setPeersDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] max-w-md" data-testid="public-peers-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-['Chivo']">Atletas na mesma categoria</DialogTitle>
          </DialogHeader>
          {peersDialog && (
            <div className="mt-1 space-y-3">
              <div className="text-xs text-[#A1A1AA]">
                <span className="text-[#71717A]">Categoria: </span>
                <span className="text-[#D4AF37]">
                  {categoryDisplay(peersDialog.athlete.athlete_age_class, peersDialog.athlete.athlete_belt, peersDialog.athlete.weight_category, peersDialog.athlete.athlete_sex, peersDialog.athlete.modality)}
                </span>
              </div>
              <div className="space-y-1.5 max-h-[55vh] overflow-y-auto" data-testid="public-peers-list">
                {peersDialog.peers.map((p, i) => (
                  <div
                    key={p._id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg ${
                      p._id === peersDialog.athlete._id
                        ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/40'
                        : 'bg-[#1A1A1A] border border-[#27272A]'
                    }`}
                  >
                    <div className="w-7 h-7 rounded-full bg-[#0A0A0A] flex items-center justify-center text-[#D4AF37] font-bold text-xs">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{p.athlete_name}</div>
                      {p.team_name && (
                        <div className="text-xs text-[#71717A] truncate">{p.team_name}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-[#71717A] text-center pt-1">
                {peersDialog.peers.length} {peersDialog.peers.length === 1 ? 'atleta' : 'atletas'} nesta categoria
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default PublicAthleteList;
